import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Star, Trophy, Euro, ShieldCheck, Megaphone, EyeOff, TrendingUp, Flame, CreditCard, Ticket, Plus, Pencil, Trash2, Send, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SubscribersPanel from "../components/SubscribersPanel";
import TransfersPanel from "../components/TransfersPanel";
import TicketsPanel from "../components/TicketsPanel";
import GlobalNoticeEditor from "../components/GlobalNoticeEditor";
import LegalPagesEditor from "../components/LegalPagesEditor";
import EmailHealthPanel from "../components/EmailHealthPanel";
import ViewAsSwitch from "../components/ViewAsSwitch";
import TermsAcceptancesPanel from "../components/TermsAcceptancesPanel";
import EmailLogPanel from "../components/EmailLogPanel";
import TesterEmailsPanel from "../components/TesterEmailsPanel";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const EMPTY_PROMO = { code: "", discount_type: "percent", discount_value: "", expires_at: "", max_uses: "", active: true };

function Stars({ n }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} style={{ width: 14, height: 14 }} className={i <= n ? "text-amber-400" : "text-slate-200"} fill={i <= n ? "currentColor" : "none"} />
      ))}
    </span>
  );
}

function AnalyticCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-card border border-slate-200/80 rounded-xl p-5 shadow-sm">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${accent}`}>
        <Icon style={{ width: 20, height: 20 }} />
      </div>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900 mt-1 leading-tight">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

const SA_TABS = [
  { value: "beta", label: "Risultati Beta Test" },
  { value: "billing", label: "Piani & Fatturato" },
  { value: "promo", label: "Codici Promo" },
  { value: "reviews", label: "Recensioni" },
  { value: "support", label: "Ticket / Assistenza" },
  { value: "legal", label: "Testi legali" },
  { value: "acceptances", label: "Accettazioni Termini" },
  { value: "emails", label: "Email inviate" },
];

export default function SuperAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState(() => {
    const h = window.location.hash.slice(1);
    return SA_TABS.some((t) => t.value === h) ? h : "beta";
  });
  const changeTab = (v) => { setTab(v); window.history.replaceState(null, "", `#${v}`); };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ analytics: {}, feedbacks: [] });
  const [billing, setBilling] = useState(null);
  const [cfgForm, setCfgForm] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoEditing, setPromoEditing] = useState(null);
  const [promoForm, setPromoForm] = useState(EMPTY_PROMO);
  const [promoSaving, setPromoSaving] = useState(false);
  const [reportEmail, setReportEmail] = useState("");
  const [savingReport, setSavingReport] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [openTickets, setOpenTickets] = useState(0);
  const loadOpenTickets = () => api.get("/super-admin/tickets/open-count").then((r) => setOpenTickets(r.data.open)).catch(() => {});
  useEffect(() => { loadOpenTickets(); const iv = setInterval(loadOpenTickets, 30000); return () => clearInterval(iv); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [bank, setBank] = useState({ holder: "", bank_name: "", iban: "", bic: "" });
  const [banner, setBanner] = useState({ enabled: false, title: "", text: "", code: "" });
  const [savingExtra, setSavingExtra] = useState(false);

  const load = async () => {
    try {
      const [fb, bl, pc, rv] = await Promise.all([
        api.get("/super-admin/beta-feedbacks"),
        api.get("/super-admin/billing"),
        api.get("/super-admin/promo-codes"),
        api.get("/super-admin/reviews"),
      ]);
      setData(fb.data);
      setBilling(bl.data);
      setCfgForm(bl.data.config);
      setReportEmail(bl.data.config.weekly_report_email || "");
      setBank(bl.data.config.owner_bank || { holder: "", bank_name: "", iban: "", bic: "" });
      setBanner(bl.data.config.promo_banner || { enabled: false, title: "", text: "", code: "" });
      setPromoCodes(pc.data);
      setReviews(rv.data);
    } catch (e) { toast.error("Errore nel caricamento"); }
    setLoading(false);
  };
  useEffect(() => { if (user && user.is_super_admin) load(); }, [user]);

  const saveBank = async () => {
    setSavingExtra(true);
    try { await api.put("/super-admin/billing", { owner_bank: bank }); toast.success("Dati bancari salvati"); await load(); }
    catch (e) { toast.error("Salvataggio non riuscito"); }
    setSavingExtra(false);
  };

  const saveBanner = async () => {
    setSavingExtra(true);
    try { await api.put("/super-admin/billing", { promo_banner: banner }); toast.success("Banner salvato"); await load(); }
    catch (e) { toast.error("Salvataggio non riuscito"); }
    setSavingExtra(false);
  };



  const deleteReview = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa recensione?")) return;
    try {
      await api.delete(`/super-admin/reviews/${id}`);
      toast.success("Recensione eliminata");
      await load();
    } catch (e) { toast.error("Operazione non riuscita"); }
  };



  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put("/super-admin/billing", {
        founder_enabled: cfgForm.founder_enabled,
        founder_seats_total: Number(cfgForm.founder_seats_total),
        founder_private: !!cfgForm.founder_private,
        founder_access_code: (cfgForm.founder_access_code || "").trim(),
        founder_annual_until: cfgForm.founder_annual_until || "",
        beta_signup_enabled: !!cfgForm.beta_signup_enabled,
      });
      toast.success("Configurazione aggiornata");
      await load();
    } catch (e) { toast.error("Salvataggio non riuscito"); }
    setSavingCfg(false);
  };

  const saveReportEmail = async () => {
    setSavingReport(true);
    try {
      await api.put("/super-admin/billing", { weekly_report_email: reportEmail.trim() });
      toast.success("Email del report aggiornata");
      await load();
    } catch (e) { toast.error("Salvataggio non riuscito"); }
    setSavingReport(false);
  };

  const sendReportNow = async () => {
    setSendingReport(true);
    try {
      const { data: r } = await api.post("/super-admin/weekly-report/send-now");
      toast.success(`Report inviato a ${r.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    setSendingReport(false);
  };

  const openNewPromo = () => { setPromoEditing(null); setPromoForm(EMPTY_PROMO); setPromoOpen(true); };
  const openEditPromo = (c) => {
    setPromoEditing(c);
    setPromoForm({
      code: c.code, discount_type: c.discount_type, discount_value: String(c.discount_value),
      expires_at: c.expires_at || "", max_uses: c.max_uses != null ? String(c.max_uses) : "", active: c.active,
    });
    setPromoOpen(true);
  };
  const savePromo = async () => {
    if (!promoForm.code.trim()) return toast.error("Inserisci il codice");
    if (!(Number(promoForm.discount_value) > 0)) return toast.error("Lo sconto deve essere maggiore di zero");
    setPromoSaving(true);
    const payload = {
      code: promoForm.code.trim().toUpperCase(), scope: "subscription",
      discount_type: promoForm.discount_type, discount_value: Number(promoForm.discount_value),
      expires_at: promoForm.expires_at || null,
      max_uses: promoForm.max_uses ? Number(promoForm.max_uses) : null, active: promoForm.active,
    };
    try {
      if (promoEditing) await api.put(`/super-admin/promo-codes/${promoEditing.id}`, payload);
      else await api.post("/super-admin/promo-codes", payload);
      toast.success(promoEditing ? "Codice aggiornato" : "Codice creato");
      setPromoOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    setPromoSaving(false);
  };
  const removePromo = async (c) => {
    if (!window.confirm(`Eliminare il codice "${c.code}"?`)) return;
    try {
      await api.delete(`/super-admin/promo-codes/${c.id}`);
      toast.success("Codice eliminato");
      await load();
    } catch (e) { toast.error("Eliminazione non riuscita"); }
  };

  if (user && user !== false && !user.is_super_admin) return <Navigate to="/dashboard" replace />;

  const toggleApprove = async (fb) => {
    const next = !fb.approved_testimonial;
    try {
      await api.post(`/super-admin/beta-feedbacks/${fb.id}/approve`, { approved: next });
      toast.success(next ? "Recensione pubblicata sulla Landing Page" : "Recensione rimossa dalla Landing Page");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const a = data.analytics || {};

  return (
    <div data-testid="super-admin-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
          <ShieldCheck style={{ width: 20, height: 20 }} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">Pannello Super-Admin</h1>
          <p className="text-sm text-slate-500">Area riservata al proprietario di MyQuoteFlow.</p>
        </div>
      </div>

      <ViewAsSwitch />
      <EmailHealthPanel defaultTo={user?.email} />

      <Tabs value={tab} onValueChange={changeTab}>
        <div className="sm:hidden mb-6">
          <Select value={tab} onValueChange={changeTab}>
            <SelectTrigger data-testid="super-admin-tab-select" className="h-11 font-semibold"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SA_TABS.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`super-admin-tab-option-${t.value}`}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <TabsList className="mb-6 h-auto flex-wrap justify-start hidden sm:flex">
          <TabsTrigger value="beta" data-testid="super-admin-tab-beta">Risultati Beta Test</TabsTrigger>
          <TabsTrigger value="billing" data-testid="super-admin-tab-billing">Piani &amp; Fatturato</TabsTrigger>
          <TabsTrigger value="promo" data-testid="super-admin-tab-promo">Codici Promo</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="super-admin-tab-reviews">Recensioni</TabsTrigger>
          <TabsTrigger value="support" data-testid="super-admin-tab-support" className="gap-1.5">Ticket / Assistenza
            {openTickets > 0 && <span data-testid="tickets-open-badge" className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold inline-flex items-center justify-center animate-pulse">{openTickets}</span>}
          </TabsTrigger>
          <TabsTrigger value="legal" data-testid="super-admin-tab-legal">Testi legali</TabsTrigger>
          <TabsTrigger value="acceptances" data-testid="super-admin-tab-acceptances">Accettazioni Termini</TabsTrigger>
          <TabsTrigger value="emails" data-testid="super-admin-tab-emails">Email inviate</TabsTrigger>
        </TabsList>
        <TabsContent value="beta">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <AnalyticCard testid="beta-analytics-rating" icon={Star} accent="bg-amber-50 text-amber-500"
                  label="Voto Medio Ricevuto" value={`${a.avg_rating ?? 0} / 5 ⭐`} sub={`su ${a.count ?? 0} tester`} />
                <AnalyticCard testid="beta-analytics-feature" icon={Trophy} accent="bg-indigo-50 text-indigo-600"
                  label="Funzionalità Più Votata" value={a.top_feature || "—"} />
                <AnalyticCard testid="beta-analytics-price" icon={Euro} accent="bg-emerald-50 text-emerald-600"
                  label="Prezzo Ideale Medio" value={a.avg_price_label || "—"} />
              </div>

              <div className="bg-card border border-slate-200/80 rounded-xl shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-border">
                  <h2 className="text-base font-semibold text-slate-900">Tutti i Feedback ({data.feedbacks.length})</h2>
                </div>
                {data.feedbacks.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-16">Nessun feedback ancora.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {data.feedbacks.map((fb) => (
                      <div key={fb.id} data-testid={`beta-feedback-row-${fb.id}`} className="p-5">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-slate-900">{fb.user_name || "—"}</span>
                              <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">{fb.profession || "—"}</Badge>
                              <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">{fb.tax_regime_label || "—"}</Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-sm text-slate-600 flex-wrap">
                              <Stars n={fb.rating} />
                              <span className="inline-flex items-center gap-1"><Trophy style={{ width: 13, height: 13 }} className="text-indigo-500" /> {fb.favorite_feature || "—"}</span>
                              <span className="inline-flex items-center gap-1 font-mono"><Euro style={{ width: 13, height: 13 }} /> {fb.ideal_price_label || fb.ideal_price}</span>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-xs text-slate-400">{fmtDate(fb.created_at)}</p>
                          </div>
                        </div>
                        {fb.suggestions && (
                          <p className="text-sm text-slate-600 mt-3 bg-slate-50 rounded-lg px-3 py-2">💡 {fb.suggestions}</p>
                        )}
                        {fb.testimonial_text && (
                          <div className="mt-3 flex items-start justify-between gap-4 flex-wrap bg-indigo-50/50 border border-indigo-100 rounded-lg px-3 py-2.5">
                            <p className="text-sm text-slate-700 italic min-w-0">"{fb.testimonial_text}"</p>
                            {fb.consent_public ? (
                              <Button size="sm" variant={fb.approved_testimonial ? "outline" : "default"}
                                data-testid={`beta-approve-${fb.id}`} onClick={() => toggleApprove(fb)} className="shrink-0">
                                {fb.approved_testimonial
                                  ? <><EyeOff style={{ width: 14, height: 14 }} className="mr-1.5" /> Nascondi dalla Landing</>
                                  : <><Megaphone style={{ width: 14, height: 14 }} className="mr-1.5" /> Approva e Mostra sulla Landing Page</>}
                              </Button>
                            ) : (
                              <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200 shrink-0">Nessun consenso pubblico</Badge>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="billing">
          {loading || !billing ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <AnalyticCard testid="billing-mrr" icon={TrendingUp} accent="bg-emerald-50 text-emerald-600"
                  label="MRR (ricavo mensile ricorrente)" value={`€ ${billing.revenue.mrr}`} sub={`ARR € ${billing.revenue.arr} · ${billing.revenue.pro_count} abbonati Pro`} />
                <AnalyticCard testid="billing-founder-revenue" icon={Flame} accent="bg-amber-50 text-amber-500"
                  label="Incassi Fondatore" value={`€ ${billing.revenue.founder_revenue}`} sub={`${billing.revenue.founder_count} membri Fondatore`} />
                <AnalyticCard testid="billing-seats" icon={CreditCard} accent="bg-indigo-50 text-indigo-600"
                  label="Posti Fondatore" value={`${billing.config.founder_seats_taken} / ${billing.config.founder_seats_total}`} sub={`${billing.config.founder_seats_left} posti rimasti`} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <AnalyticCard testid="billing-gross" icon={Euro} accent="bg-emerald-50 text-emerald-600"
                  label="Incassi lordi" value={`€ ${billing.revenue.gross_collected ?? 0}`} sub="Somma pagamenti abbonamenti" />
                <AnalyticCard testid="billing-refunds" icon={Euro} accent="bg-red-50 text-red-500"
                  label="Rimborsi erogati" value={`− € ${billing.revenue.refunds_total ?? 0}`} sub="Totali + parziali (registrati)" />
                <AnalyticCard testid="billing-net" icon={TrendingUp} accent="bg-indigo-50 text-indigo-600"
                  label="Incassi netti" value={`€ ${billing.revenue.net_collected ?? 0}`} sub="Lordo − rimborsi" />
              </div>
              <GlobalNoticeEditor />
              <SubscribersPanel onChanged={load} />
              <TransfersPanel onChanged={load} />

              <div className="bg-card border border-slate-200/80 rounded-xl shadow-sm p-6 max-w-xl">
                <h2 className="text-base font-semibold text-slate-900 mb-4">Gestione Offerta Fondatore e Fase Beta</h2>
                <div className="flex items-center justify-between gap-4 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Iscrizioni Beta Tester</p>
                    <p className="text-xs text-slate-500">Se attiva, i nuovi iscritti ricevono il piano Beta Tester (Pro gratuito). Dopo 5 preventivi devono lasciare la recensione e poi scegliere un piano a pagamento.</p>
                  </div>
                  <Switch data-testid="beta-signup-switch" checked={!!cfgForm.beta_signup_enabled}
                    onCheckedChange={(v) => setCfgForm((s) => ({ ...s, beta_signup_enabled: v }))} />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border p-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Mostra l'Offerta Fondatore</p>
                    <p className="text-xs text-slate-500">Se disattivata, la card Fondatore sparisce da Landing e /billing.</p>
                  </div>
                  <Switch data-testid="founder-enabled-switch" checked={!!cfgForm.founder_enabled}
                    onCheckedChange={(v) => setCfgForm((s) => ({ ...s, founder_enabled: v }))} />
                </div>
                <div className="rounded-lg border border-border p-4 mb-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900">Offerta riservata ai tester</p>
                      <p className="text-xs text-slate-500">La card Fondatore compare solo a chi inserisce il codice tester in /billing.</p>
                    </div>
                    <Switch data-testid="founder-private-switch" checked={!!cfgForm.founder_private}
                      onCheckedChange={(v) => setCfgForm((s) => ({ ...s, founder_private: v }))} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Codice tester</label>
                    <Input data-testid="founder-access-code-input" value={cfgForm.founder_access_code || ""} maxLength={60}
                      onChange={(e) => setCfgForm((s) => ({ ...s, founder_access_code: e.target.value }))} className="font-mono mt-1.5" placeholder="es. TESTER2026" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fine primo anno Fondatore (prevendita)</label>
                  <Input type="date" data-testid="founder-annual-until-input" value={cfgForm.founder_annual_until || ""}
                    onChange={(e) => setCfgForm((s) => ({ ...s, founder_annual_until: e.target.value }))} className="font-mono mt-1.5 sm:w-56" />
                  <p className="text-xs text-slate-500 mt-1">Fino a questa data il Fondatore annuale si paga una sola volta e scade in questo giorno. Lascia vuoto per disattivare la prevendita.</p>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Posti totali</label>
                    <Input type="number" min="0" data-testid="founder-seats-total-input" value={cfgForm.founder_seats_total}
                      onChange={(e) => setCfgForm((s) => ({ ...s, founder_seats_total: e.target.value }))} className="font-mono mt-1.5" />
                  </div>
                  <div className="self-end pb-2">
                    <p data-testid="founder-seats-taken-readonly" className="text-sm text-slate-600">
                      Posti assegnati: <span className="font-mono font-semibold text-slate-900">{billing.config.founder_seats_taken}</span> (calcolati dai Fondatori attivi)
                    </p>
                  </div>
                </div>
                <Button data-testid="save-founder-config" onClick={saveCfg} disabled={savingCfg} className="font-semibold">
                  {savingCfg ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva configurazione"}
                </Button>
              </div>

              <TesterEmailsPanel config={billing.config} />

              <div className="bg-card border border-slate-200/80 rounded-xl shadow-sm p-6 max-w-xl mt-6">
                <h2 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2"><Mail style={{ width: 18, height: 18 }} className="text-primary" /> Report Settimanale</h2>
                <p className="text-xs text-slate-500 mb-4">Ricevi ogni Lunedì mattina un riepilogo email con MRR, nuovi iscritti e feedback beta. Lascia vuoto per usare la tua email Super-Admin.</p>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email destinatario</label>
                <div className="flex gap-2 mt-1.5 mb-4">
                  <Input data-testid="report-email-input" type="email" value={reportEmail}
                    onChange={(e) => setReportEmail(e.target.value)} placeholder="es. tu@tuodominio.it" />
                  <Button data-testid="report-email-save" onClick={saveReportEmail} disabled={savingReport} variant="outline" className="shrink-0 font-semibold">
                    {savingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
                  </Button>
                </div>
                <Button data-testid="report-send-now" onClick={sendReportNow} disabled={sendingReport} className="font-semibold">
                  {sendingReport ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send style={{ width: 16, height: 16 }} className="mr-1.5" />}
                  Invia Report Ora
                </Button>
              </div>

              <div className="bg-card border border-slate-200/80 rounded-xl shadow-sm p-6 max-w-xl mt-6 space-y-4" data-testid="owner-bank-card">
                <h2 className="text-base font-semibold text-slate-900">Dati Bancari Proprietario (Incasso Abbonamenti)</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input data-testid="owner-bank-holder" value={bank.holder || ""} onChange={(e) => setBank((s) => ({ ...s, holder: e.target.value }))} placeholder="Intestatario Conto" />
                  <Input data-testid="owner-bank-name" value={bank.bank_name || ""} onChange={(e) => setBank((s) => ({ ...s, bank_name: e.target.value }))} placeholder="Nome Banca" />
                  <Input data-testid="owner-bank-iban" value={bank.iban || ""} onChange={(e) => setBank((s) => ({ ...s, iban: e.target.value }))} placeholder="IBAN" className="font-mono" />
                  <Input data-testid="owner-bank-bic" value={bank.bic || ""} onChange={(e) => setBank((s) => ({ ...s, bic: e.target.value }))} placeholder="BIC / SWIFT" className="font-mono" />
                </div>
                <Button data-testid="save-bank" onClick={saveBank} disabled={savingExtra} className="font-semibold">
                  {savingExtra ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Salva Dati Bancari
                </Button>
              </div>

              <div className="bg-card border border-slate-200/80 rounded-xl shadow-sm p-6 max-w-xl mt-6 space-y-4" data-testid="promo-banner-card">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-slate-900">Banner Promo / Offerte Abbonamenti</h2>
                  <Switch data-testid="promo-banner-enabled" checked={!!banner.enabled} onCheckedChange={(v) => setBanner((s) => ({ ...s, enabled: v }))} />
                </div>
                <Input data-testid="promo-banner-title" value={banner.title || ""} onChange={(e) => setBanner((s) => ({ ...s, title: e.target.value }))} placeholder="Titolo offerta (es. Offerta di lancio!)" />
                <Textarea data-testid="promo-banner-text" rows={2} value={banner.text || ""} onChange={(e) => setBanner((s) => ({ ...s, text: e.target.value }))} placeholder="Testo dell'offerta" />
                <Input data-testid="promo-banner-code" value={banner.code || ""} onChange={(e) => setBanner((s) => ({ ...s, code: e.target.value.toUpperCase() }))} placeholder="Codice sconto evidenziato (es. LANCIO30)" className="font-mono uppercase" />
                <Button data-testid="save-banner" onClick={saveBanner} disabled={savingExtra} className="font-semibold">
                  {savingExtra ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Salva Banner
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="promo">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                <div>
                  <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Ticket style={{ width: 18, height: 18 }} className="text-primary" /> Codici Promo Abbonamenti (globali)</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Codici applicabili al checkout dei piani Pro e Fondatore.</p>
                </div>
                <Button data-testid="sa-promo-new-button" onClick={openNewPromo} className="font-semibold"><Plus style={{ width: 16, height: 16 }} className="mr-1.5" /> Nuovo Codice</Button>
              </div>
              {promoCodes.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-card p-12 text-center"><p className="text-slate-400">Nessun codice promo globale.</p></div>
              ) : (
                <div className="bg-card border border-slate-200/80 rounded-xl shadow-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-border">
                        <th className="px-5 py-3 font-semibold">Codice</th>
                        <th className="px-5 py-3 font-semibold">Sconto</th>
                        <th className="px-5 py-3 font-semibold">Scadenza</th>
                        <th className="px-5 py-3 font-semibold">Utilizzi</th>
                        <th className="px-5 py-3 font-semibold">Stato</th>
                        <th className="px-5 py-3 font-semibold text-right">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {promoCodes.map((c) => (
                        <tr key={c.id} data-testid={`sa-promo-row-${c.code}`} className="hover:bg-slate-50/60">
                          <td className="px-5 py-3 font-mono font-bold text-slate-900">{c.code}</td>
                          <td className="px-5 py-3 text-slate-700">{c.discount_type === "percent" ? `${c.discount_value}%` : `€ ${c.discount_value}`}</td>
                          <td className="px-5 py-3 text-slate-500">{c.expires_at ? fmtDate(c.expires_at) : "—"}</td>
                          <td className="px-5 py-3 text-slate-500 font-mono">{c.uses_count}{c.max_uses != null ? ` / ${c.max_uses}` : ""}</td>
                          <td className="px-5 py-3">
                            {c.active
                              ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Attivo</Badge>
                              : <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">Disattivato</Badge>}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" data-testid={`sa-promo-edit-${c.code}`} onClick={() => openEditPromo(c)}><Pencil style={{ width: 15, height: 15 }} /></Button>
                              <Button size="sm" variant="ghost" data-testid={`sa-promo-delete-${c.code}`} onClick={() => removePromo(c)} className="text-red-500 hover:text-red-600 hover:bg-red-50"><Trash2 style={{ width: 15, height: 15 }} /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </TabsContent>
        <TabsContent value="reviews">
          <div className="bg-card border border-slate-200/80 rounded-xl shadow-sm p-5 mb-5">
            <p className="text-sm font-semibold text-slate-900">Pubblicazione automatica trasparente</p>
            <p className="text-xs text-slate-500">Tutte le recensioni degli utenti registrati vengono pubblicate subito in Landing Page e conteggiate nella media. Qui puoi solo eliminare recensioni di test o spam.</p>
          </div>
          {reviews.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-card p-12 text-center"><p className="text-slate-400">Nessuna recensione ricevuta.</p></div>
          ) : (
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-card border border-slate-200/80 rounded-xl p-4 flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{r.name || "Utente"}</span>
                      <span className="text-xs text-slate-400">{r.role}</span>
                      <span className="text-amber-500 text-sm">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Pubblicata</Badge>
                      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("it-IT")}</span>
                    </div>
                    {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`review-delete-${r.id}`} size="sm" variant="outline" onClick={() => deleteReview(r.id)} className="text-red-600 border-red-200">Elimina</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="support">
          <TicketsPanel onChanged={loadOpenTickets} />
        </TabsContent>

        <TabsContent value="emails">
          <EmailLogPanel />
        </TabsContent>
        <TabsContent value="acceptances">
          <TermsAcceptancesPanel />
        </TabsContent>
        <TabsContent value="legal">
          <LegalPagesEditor />
        </TabsContent>
      </Tabs>

      <Dialog open={promoOpen} onOpenChange={setPromoOpen}>
        <DialogContent className="max-w-md" data-testid="sa-promo-dialog">
          <DialogHeader><DialogTitle>{promoEditing ? "Modifica Codice Globale" : "Nuovo Codice Globale"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Codice</label>
              <Input data-testid="sa-promo-form-code" value={promoForm.code} disabled={!!promoEditing}
                onChange={(e) => setPromoForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))}
                placeholder="Es. QUOTEFLOW20" className="font-mono uppercase mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo sconto</label>
                <Select value={promoForm.discount_type} onValueChange={(v) => setPromoForm((s) => ({ ...s, discount_type: v }))}>
                  <SelectTrigger data-testid="sa-promo-form-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentuale (%)</SelectItem>
                    <SelectItem value="amount">Importo fisso (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Valore</label>
                <Input data-testid="sa-promo-form-value" type="number" min="0" value={promoForm.discount_value}
                  onChange={(e) => setPromoForm((s) => ({ ...s, discount_value: e.target.value }))}
                  placeholder={promoForm.discount_type === "percent" ? "20" : "30"} className="font-mono mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Scadenza (facolt.)</label>
                <Input data-testid="sa-promo-form-expires" type="date" value={promoForm.expires_at}
                  onChange={(e) => setPromoForm((s) => ({ ...s, expires_at: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Max utilizzi (facolt.)</label>
                <Input data-testid="sa-promo-form-maxuses" type="number" min="0" value={promoForm.max_uses}
                  onChange={(e) => setPromoForm((s) => ({ ...s, max_uses: e.target.value }))}
                  placeholder="illimitati" className="font-mono mt-1.5" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <span className="text-sm font-medium text-slate-700">Codice attivo</span>
              <Switch data-testid="sa-promo-form-active" checked={promoForm.active} onCheckedChange={(v) => setPromoForm((s) => ({ ...s, active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoOpen(false)}>Annulla</Button>
            <Button data-testid="sa-promo-form-save" onClick={savePromo} disabled={promoSaving} className="font-semibold">
              {promoSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
